import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import './Login.css';

function HeraclesBadgeSVG() {
  return (
    <svg viewBox="0 0 200 238" xmlns="http://www.w3.org/2000/svg" className="heracles-badge">
      <defs>
        <clipPath id="badge-outer-clip">
          <path d="M5,4 L195,4 L195,152 L100,234 L5,152 Z" />
        </clipPath>
        <clipPath id="badge-inner-clip">
          <path d="M11,10 L189,10 L189,150 L100,228 L11,150 Z" />
        </clipPath>
      </defs>

      {/* White outer border fill */}
      <path d="M5,4 L195,4 L195,152 L100,234 L5,152 Z" fill="white" />

      {/* Black inner shield + stripes */}
      <g clipPath="url(#badge-inner-clip)">
        <rect x="0" y="0" width="200" height="238" fill="#111" />
        {/* Three white vertical stripes */}
        <rect x="33"  y="52" width="30" height="200" fill="white" />
        <rect x="85"  y="52" width="30" height="200" fill="white" />
        <rect x="137" y="52" width="30" height="200" fill="white" />
      </g>

      {/* HERACLES top banner */}
      <g clipPath="url(#badge-inner-clip)">
        <rect x="0" y="0" width="200" height="54" fill="#111" />
        <text x="100" y="41" textAnchor="middle" fill="white"
              fontFamily="'Arial Black', Impact, sans-serif"
              fontSize="27" fontWeight="900" letterSpacing="3">
          HERACLES
        </text>
      </g>

      {/* 1903 */}
      <text x="100" y="72" textAnchor="middle" fill="white"
            fontFamily="Georgia, serif" fontSize="13">
        1903
      </text>

      {/* White circle (double border) */}
      <circle cx="100" cy="128" r="54" fill="white" />
      <circle cx="100" cy="128" r="54" fill="none" stroke="#111" strokeWidth="3" />
      <circle cx="100" cy="128" r="49" fill="none" stroke="#111" strokeWidth="1.2" />

      {/* Lion silhouette — geometric polygon style, facing right */}
      <path d="
        M 78,162
        L 68,150 L 60,133 L 55,118 L 59,104
        L 65,94  L 72,86  L 81,80  L 92,77
        L 103,78 L 113,83 L 119,91
        L 123,101 L 125,113 L 126,124
        L 123,134 L 116,141 L 108,147
        L 99,151  L 89,155  L 80,159 Z
      " fill="#111" />

      {/* Mane geometric facets (lighter tone to show depth) */}
      <path d="M 55,118 L 60,133 L 67,125 Z" fill="#2a2a2a" />
      <path d="M 59,104 L 55,118 L 63,113 Z" fill="#2a2a2a" />
      <path d="M 65,94  L 59,104 L 67,102 Z" fill="#2a2a2a" />
      <path d="M 72,86  L 65,94  L 73,93  Z" fill="#2a2a2a" />
      <path d="M 81,80  L 72,86  L 80,87  Z" fill="#2a2a2a" />

      {/* Face polygon facets */}
      <path d="M 119,91  L 123,101 L 120,95  Z" fill="#2a2a2a" />
      <path d="M 123,101 L 125,113 L 121,107 Z" fill="#333" />
      <path d="M 125,113 L 126,124 L 122,118 Z" fill="#2a2a2a" />
      <path d="M 126,124 L 123,134 L 120,128 Z" fill="#333" />

      {/* Eye */}
      <ellipse cx="114" cy="101" rx="5" ry="4.5" fill="white" />
      <circle  cx="114" cy="101" r="2.5" fill="#111" />

      {/* Inner face highlight */}
      <path d="M 92,77 L 103,78 L 113,83 L 117,89 L 108,84 L 98,81 Z" fill="#1a1a1a" />

      {/* Cyan diamonds at bottom (3: two top, one below-center) */}
      <polygon points="74,192 86,176 98,192 86,208"  fill="#00e5e5" />
      <polygon points="88,188 100,172 112,188 100,204" fill="#00e5e5" />
      <polygon points="102,194 114,178 126,194 114,210" fill="#00e5e5" />

      {/* Outer border strokes */}
      <path d="M5,4 L195,4 L195,152 L100,234 L5,152 Z"
            fill="none" stroke="white" strokeWidth="2.5" />
      <path d="M11,10 L189,10 L189,150 L100,228 L11,150 Z"
            fill="none" stroke="#444" strokeWidth="1" />
    </svg>
  );
}

function HeraclesBadge() {
  const [useSvg, setUseSvg] = useState(false);
  if (useSvg) return <HeraclesBadgeSVG />;
  return (
    <img
      src="/heracles-crest.png"
      className="heracles-badge"
      alt="Heracles Almelo"
      onError={() => setUseSvg(true)}
    />
  );
}

export default function Login({ onLogin }) {
  const [username, setUsername]         = useState('');
  const [password, setPassword]         = useState('');
  const [error, setError]               = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async event => {
    event.preventDefault();
    setError(null);
    const authError = await onLogin(username.trim(), password);
    if (authError) {
      setError(authError);
      return;
    }
    navigate('/app', { replace: true });
  };

  const stadiumBg = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/stadium.jpg)`,
  };

  return (
    <div className="login-page" style={stadiumBg}>
      <div className="login-brand">
        <div className="brand-top">
          <HeraclesBadge />
          <p className="brand-club-name">HERACLES ALMELO</p>
          <h1 className="brand-logo">
            HeraLab<span className="brand-logo-x">X</span>
          </h1>
          <p className="brand-tagline">ELITE &bull; DATA &bull; PERFORMANCE</p>
        </div>

        <div className="brand-partner">
          <img
            src={`${process.env.PUBLIC_URL}/asito-logo.png`}
            alt="Asito"
            className="partner-logo"
          />
        </div>
      </div>

      <div className="login-card-wrap">
        <div className="login-card">
          <div className="lc-header">
            <h2>WELCOME BACK</h2>
            <p>Sign in to access the HeraLabX platform<br />for Heracles Almelo Physical Performance.</p>
            <div className="lc-divider" />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="lc-field">
              <label>USERNAME</label>
              <div className="lc-input-wrap">
                <User size={17} className="lc-icon" />
                <input
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  spellCheck="false"
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="lc-field">
              <label>PASSWORD</label>
              <div className="lc-input-wrap">
                <Lock size={17} className="lc-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  spellCheck="false"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lc-eye-btn"
                  onClick={() => setShowPassword(p => !p)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <Eye size={17} /> : <EyeOff size={17} />}
                </button>
              </div>
            </div>

            <div className="lc-forgot">
              <button type="button" className="lc-forgot-btn">Forgot password?</button>
            </div>

            {error && <div className="lc-error">{error}</div>}

            <button type="submit" className="lc-submit">
              LOGIN <span className="lc-arrow">›</span>
            </button>
          </form>

          <p className="lc-footer">
            Only authorized personnel<br />are permitted to access this system.
          </p>
        </div>
      </div>
    </div>
  );
}
